import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import { useHasPermission } from '@/components/PermissionGate';
import { SetupPackageBuildDialog } from '@/core/SetupPackageBuildDialog';
import type { ListViewExtraProps } from '@/config/listViewExtras';

/**
 * "Save integration as package…" on the integration admin lists (spec:
 * docs/15-designspecs/Setup-Packages.md, Phase 3): packages the rows selected in the list with
 * what they depend on — a client's granted jobs, a job's connection. Applied elsewhere, the
 * selected records update their namesakes; what they depend on is created only where it is
 * missing. Connection strings never travel. Nothing selected, nothing to package: the whole
 * setup is the environment copy's, built through the API without a selection.
 */
export function SaveIntegrationSetupButton({ entity, selectedIds }: ListViewExtraProps) {
  const { t } = useTranslation();
  const canBuild = useHasPermission('Core.SetupPackage.Create');
  const [open, setOpen] = useState(false);

  if (!canBuild) return null;

  return (
    <>
      <Button
        startIcon={<Inventory2Outlined />}
        disabled={!entity || selectedIds.length === 0}
        onClick={() => setOpen(true)}
        sx={{ textTransform: 'none', color: 'text.secondary' }}
      >
        {t('Integration.SaveSetupAsPackage.Button', { defaultValue: 'Save integration as package…' })}
      </Button>
      {entity && (
        <SetupPackageBuildDialog
          moduleName="Integration"
          rootEntityName="IntegrationSetup"
          selection={{ entityName: entity, ids: selectedIds }}
          title={t('Integration.SaveSetupAsPackage.Title', { defaultValue: 'Save integration as package' })}
          intro={t('Integration.SaveSetupAsPackage.Intro', {
            defaultValue: 'Packages the {{count}} selected records with what they depend on: a client\'s granted jobs, a job\'s connection. Where the package is applied, the selected records update the records of the same name; what they depend on is created where it is missing and left as it is where it exists. Connection strings are not included.',
            count: selectedIds.length,
          })}
          open={open}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
